import React, { useEffect, useMemo, useState } from "react";
import { listProspects, listCampaigns, PROSPECT_STATUSES, STATUS_COLORS } from "@/lib/chiroApi";
import ProspectDetail from "@/components/ProspectDetail";
import ScorePill from "@/components/ScorePill";

export default function Prospects() {
    const [campaigns, setCampaigns] = useState([]);
    const [prospects, setProspects] = useState([]);
    const [filterCampaign, setFilterCampaign] = useState("");
    const [filterCity, setFilterCity] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [search, setSearch] = useState("");
    const [sortByOpp, setSortByOpp] = useState(false);
    const [selected, setSelected] = useState(null);
    const [openDetail, setOpenDetail] = useState(false);

    const load = async () => {
        const params = {};
        if (filterCampaign) params.campaign_id = filterCampaign;
        if (filterCity) params.city = filterCity;
        if (filterStatus) params.status = filterStatus;
        const [c, p] = await Promise.all([listCampaigns(), listProspects(params)]);
        setCampaigns(c);
        setProspects(p);
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterCampaign, filterCity, filterStatus]);

    const cities = useMemo(() => {
        const s = new Set(prospects.map((p) => p.city).filter(Boolean));
        return Array.from(s).sort();
    }, [prospects]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        const base = !q
            ? prospects
            : prospects.filter(
                  (p) =>
                      (p.practice_name || "").toLowerCase().includes(q) ||
                      (p.website || "").toLowerCase().includes(q) ||
                      (p.email || "").toLowerCase().includes(q),
              );
        if (sortByOpp) {
            // Stable copy + sort. Null opportunity sorts to the bottom.
            return [...base].sort((a, b) => {
                const av = a.opportunity_score ?? -1;
                const bv = b.opportunity_score ?? -1;
                return bv - av;
            });
        }
        return base;
    }, [prospects, search, sortByOpp]);

    const campaignName = (id) => campaigns.find((c) => c.id === id)?.name || "—";

    return (
        <div data-testid="prospects-page" className="max-w-6xl">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7cc93c] mb-2">
                // prospects · all campaigns
            </div>
            <h1 className="text-3xl font-bold tracking-tight mb-4">Prospects</h1>

            <div className="grid grid-cols-4 gap-3 mb-4">
                <select
                    value={filterCampaign}
                    onChange={(e) => setFilterCampaign(e.target.value)}
                    data-testid="prospects-filter-campaign"
                    className="bg-[#0a0b0d] border border-[#262a2e] rounded-[2px] px-3 py-2 font-mono text-[11px] text-[#f3f4f6]"
                >
                    <option value="">All campaigns</option>
                    {campaigns.map((c) => (
                        <option key={c.id} value={c.id}>
                            {c.name}
                        </option>
                    ))}
                </select>
                <select
                    value={filterCity}
                    onChange={(e) => setFilterCity(e.target.value)}
                    data-testid="prospects-filter-city"
                    className="bg-[#0a0b0d] border border-[#262a2e] rounded-[2px] px-3 py-2 font-mono text-[11px] text-[#f3f4f6]"
                >
                    <option value="">All cities</option>
                    {cities.map((c) => (
                        <option key={c} value={c}>
                            {c}
                        </option>
                    ))}
                </select>
                <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    data-testid="prospects-filter-status"
                    className="bg-[#0a0b0d] border border-[#262a2e] rounded-[2px] px-3 py-2 font-mono text-[11px] text-[#f3f4f6]"
                >
                    <option value="">All statuses</option>
                    {PROSPECT_STATUSES.map((s) => (
                        <option key={s} value={s}>
                            {s}
                        </option>
                    ))}
                </select>
                <input
                    type="text"
                    placeholder="Search name / website / email"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    data-testid="prospects-search"
                    className="bg-[#0a0b0d] border border-[#262a2e] rounded-[2px] px-3 py-2 font-mono text-[11px] text-[#f3f4f6]"
                />
            </div>

            <div className="font-mono text-[11px] text-[#9ca3af] mb-2 flex items-center justify-between">
                <span>
                    {filtered.length} of {prospects.length}
                </span>
                <button
                    onClick={() => setSortByOpp((s) => !s)}
                    data-testid="prospects-sort-by-opp"
                    className={`font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 border rounded-[2px] transition-colors ${
                        sortByOpp
                            ? "border-[#7cc93c] text-[#7cc93c]"
                            : "border-[#262a2e] text-[#9ca3af] hover:text-[#f3f4f6]"
                    }`}
                >
                    Sort by Opportunity ↓ {sortByOpp ? "ON" : "OFF"}
                </button>
            </div>

            {filtered.length === 0 && (
                <div
                    className="p-8 border border-dashed border-[#262a2e] text-center text-[#9ca3af] font-mono text-[12px]"
                    data-testid="prospects-empty"
                >
                    No prospects match these filters.
                </div>
            )}

            {filtered.length > 0 && (
                <div
                    className="border border-[#262a2e] rounded-[2px] divide-y divide-[#262a2e]"
                    data-testid="prospects-list"
                >
                    {filtered.map((p) => (
                        <button
                            key={p.id}
                            onClick={() => {
                                setSelected(p);
                                setOpenDetail(true);
                            }}
                            data-testid={`prospect-row-${p.id}`}
                            className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-[#0f1113]"
                        >
                            <div className="flex-1 min-w-0">
                                <div className="font-bold text-[13px] truncate flex items-center gap-2">
                                    {p.practice_name}
                                    {p.is_dry_run && (
                                        <span
                                            className="font-mono text-[8px] uppercase tracking-[0.2em] text-[#0a84ff] border border-[#0a84ff]/40 px-1 py-[1px] rounded-[2px] shrink-0"
                                            data-testid={`prospect-dry-run-pill-${p.id}`}
                                        >
                                            DRY RUN
                                        </span>
                                    )}
                                    {p.disqualified && (
                                        <span
                                            className="font-mono text-[8px] uppercase tracking-[0.2em] text-[#ff453a] border border-[#ff453a]/40 px-1 py-[1px] rounded-[2px] shrink-0"
                                            data-testid={`prospect-disqualified-pill-${p.id}`}
                                        >
                                            DISQUALIFIED
                                        </span>
                                    )}
                                </div>
                                <div className="font-mono text-[10px] text-[#9ca3af] truncate">
                                    {p.city || "—"}, {p.state || "—"} {p.zip || ""} ·{" "}
                                    {campaignName(p.campaign_id)}
                                </div>
                            </div>
                            <ScorePill
                                label="Fit"
                                value={p.fit_score}
                                testid={`prospect-fit-${p.id}`}
                            />
                            <ScorePill
                                label="Opp"
                                value={p.opportunity_score}
                                testid={`prospect-opp-${p.id}`}
                            />
                            {p.google_rating && (
                                <div className="font-mono text-[10px] text-[#9ca3af] shrink-0">
                                    ★ {p.google_rating} ({p.google_user_rating_count || 0})
                                </div>
                            )}
                            <span
                                className="font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-[2px] border rounded-[2px] shrink-0"
                                style={{
                                    color: STATUS_COLORS[p.status] || "#9ca3af",
                                    borderColor: (STATUS_COLORS[p.status] || "#262a2e") + "55",
                                }}
                            >
                                {p.status}
                            </span>
                        </button>
                    ))}
                </div>
            )}

            {selected && (
                <ProspectDetail
                    open={openDetail}
                    onOpenChange={setOpenDetail}
                    prospect={selected}
                    onChanged={(p) => {
                        setProspects((list) => list.map((x) => (x.id === p.id ? p : x)));
                        setSelected(p);
                    }}
                    onDeleted={(pid) => {
                        setProspects((list) => list.filter((x) => x.id !== pid));
                        setOpenDetail(false);
                    }}
                />
            )}
        </div>
    );
}
